'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Home, RefreshCw } from 'lucide-react'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Track if we've already reported this error to avoid duplicate reports
  const reportedRef = useRef(false)

  useEffect(() => {
    // Only report once per error instance
    if (reportedRef.current) return
    reportedRef.current = true

    // Log the error to Sentry with isolated scope to prevent tag pollution
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'app')
      if (error.digest) {
        scope.setExtra('digest', error.digest)
      }
      Sentry.captureException(error)
    })

    // Cleanup: reset the reported flag when error changes
    return () => {
      reportedRef.current = false
    }
  }, [error])

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Oops! Something went wrong
          </h1>
          <p className="text-muted-foreground">
            We encountered an unexpected error. Don&apos;t worry, our team has been notified.
          </p>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 p-4 bg-destructive/10 rounded-lg text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-destructive/80 mt-2">
                Digest: {error.digest}
              </p>
            )}
          </div>
        )}

        {error.digest && process.env.NODE_ENV !== 'development' && (
          <p className="text-xs text-muted-foreground mb-6">
            Reference: {error.digest}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={reset}
            variant="default"
            className="gap-2"
            aria-label="Try loading the page again"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </Button>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="gap-2"
            aria-label="Go to home page"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Go home
          </Button>
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  )
}
